import { useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { DashboardAdmin } from './pages/DashboardAdmin';
import { DashboardClient } from './pages/DashboardClient';
import { Organizations } from './pages/Organizations';
import { Learners } from './pages/Learners';
import { Programs } from './pages/Programs';
import { Support } from './pages/Support';
import { Projects } from './pages/Projects';
import { Settings } from './pages/Settings';
import { Landing } from './pages/Landing';
import { ResetPassword } from './pages/ResetPassword';
import { useAuth } from './contexts/AuthContext';
import { Sidebar } from './components/Layout/Sidebar';
import { LoadingSpinner } from './components/UI/LoadingSpinner';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';

type PageId = 'dashboard' | 'organizations' | 'learners' | 'programs' | 'projects' | 'support' | 'settings';

function AppContent() {
  const [currentPage, setCurrentPage] = useState<PageId>('dashboard');
  const { user, loading, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const { isCollapsed } = useSidebar();

  // Page de réinitialisation de mot de passe (accessible sans connexion)
  if (location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Landing />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return isSuperAdmin ? <DashboardAdmin /> : <DashboardClient />;
      case 'organizations':
        // Only super admin can access
        return isSuperAdmin ? <Organizations /> : <DashboardClient />;
      case 'learners':
        return <Learners />;
      case 'programs':
        // Both super admin and clients can access (clients in read-only mode)
        return <Programs />;
      case 'projects':
        return <Projects />;
      case 'support':
        return <Support />;
      case 'settings':
        // Only super admin can access
        return isSuperAdmin ? <Settings /> : <DashboardClient />;
      default:
        return <div className="p-8 text-center text-gray-500">Page en construction</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Sidebar
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page as PageId)}
        onLogout={logout}
      />
      <main className={`p-4 transition-all duration-300 ease-in-out ${isCollapsed ? 'pl-20' : 'pl-68'}`}
        style={{ paddingLeft: isCollapsed ? '5rem' : '17rem' }}>
        <div className="max-w-7xl mx-auto">
          {renderPage()}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <SidebarProvider>
        <AppContent />
      </SidebarProvider>
    </Router>
  );
}

export default App;
